import {
  isGroup,
  type IActivityOccurrenceOrGroup,
  type IGroupOfActivityOccurrence,
} from "@medoma/models/activities";
import {
  getPatientFullName,
  patientStatusSchema,
  type IDeletedPatient,
  type IExistingPatient,
} from "@medoma/models/patients";
import type { IVisit } from "@medoma/models/visits";
import { filterByNameTerms } from "@medoma/components/SearchField/searchUtils";

/**
 * Determine if the status for an activity occurrence should be shown on each activity occurrence title.
 * @param group - The group to check
 * @returns
 */
export const showStatusForActivityOccurrence = ({
  group,
}: {
  group?: Pick<IGroupOfActivityOccurrence, "occurrences">;
}) => {
  // Per-activity status only applies to occurrences in a group with multiple occurrences;
  // single activities only use status from status column.
  if (group === undefined || group.occurrences.length <= 1) {
    return false;
  }

  const statuses = group.occurrences.map((o) => o.status);
  const allSame = statuses.every((s) => s === statuses[0]);
  // Same status everywhere: no need to show status on each activity title.
  // Status column is enough.
  if (allSame) {
    return false;
  }

  // Mixed statuses: show each row’s status for disambiguation.
  return true;
};

export const matchesPatientOrActivityTitleSearch = (
  filterValue: string,
  patient:
    | Pick<IExistingPatient, "name" | "status">
    | Pick<IDeletedPatient, "status">
    | null
    | undefined,
  activityOccurrenceOrGroup: IActivityOccurrenceOrGroup | IVisit,
): boolean => {
  if (filterValue === "") {
    return true;
  }

  const searchValue = filterValue.toLocaleLowerCase();

  const patientMatchesSearch =
    patient?.status !== patientStatusSchema.enum.deleted &&
    patient?.name &&
    filterByNameTerms(getPatientFullName(patient.name), filterValue);

  if (patientMatchesSearch) {
    return true;
  }

  if (isGroup(activityOccurrenceOrGroup)) {
    return activityOccurrenceOrGroup.occurrences.some((occurrence) =>
      occurrence.title.toLocaleLowerCase().includes(searchValue),
    );
  } else {
    return activityOccurrenceOrGroup.title
      .toLocaleLowerCase()
      .includes(searchValue);
  }
};
